<template>
    <div :data-theme="currentTheme">
        <div class="map_content">
            <div class="map_loader">
                <div class="sk-cube-grid">
                    <button class="btn btn-square loading"></button>
                </div>
            </div>
            <div
                class="absolute top-0 left-1 text-ellipsis overflow-hidden z-20 my-1 ml-1 sm:left-24 text-left card card-compact m-2 map-logo-title drop-shadow-lg">
                <div class="card p-2">
                    <a :href="displayLink">
                        <div class="flex items-center">
                            <div
                                class="avatar"
                                v-if="mapData.header_type === 2 || mapData.header_type === 3"
                            >
                                <div class="w-10 h-10 rounded">
                                    <img :src="mapData.logo_url" />
                                </div>
                            </div>
                            <div
                                class="flex flex-col items-start text-sm mx-2"
                                v-if="mapData.header_type === 1 || mapData.header_type === 3"
                            >
                                <div class="text-base-content font-bold">{{ mapData.title }}</div>
                                <div class="text-base-content/80 font-bold text-xs">{{ mapData.sub_title }}</div>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
            <div class="absolute top-0 right-1 m-1 z-20">
                <parcel-nav
                    :layer-title="currentLayer.title"
                    :parcel-title="parcelResource.title"
                    :display-link="displayLink"
                    :go-to-main-map="goToMainMap"
                    :trans="trans"
                    :has-parent-id="parentId ? true : false"
                    :go-to-parent-parcel-map="goToParentParcelMap"
                    :other-parcels="otherParcels"
                    :parcel-id="parcelResource.id"
                    :categoryId="categoryId"
                ></parcel-nav>
            </div>
            <side-menu
                :show-zoom-btn="show.zoomBtn"
                :show-fullscreen="mapData.show_fullscreen"
                :show-switch-language="mapData.is_multi_localization"
                :show-refresh-btn="show.refreshBtn"
                :on-click-zoom="onClickZoom"
                :go-to-main-map="goToMainMap"
                @toggled="openSubSidebar"
                :show-new-tab="mapData.show_new_tab"
                :display-link="displayLink"
                :trans="trans"
                :map-data="mapData"
                :layer-title="currentLayer.title"
                :parcel-title="parcelResource.title"
                :locales="mapData.locales"
                :current-locale="mapData.current_locale"
                :categoryId="categoryId"
            ></side-menu>
            <div class="map_iframe">
                <div
                    id="map"
                    style="height: 100%"
                    class="mw-75 mh-75"
                ></div>
            </div>
            <side-bar-navi
                @toggledSubSidebar="openSubSidebar"
                :open="open"
                :trans="trans"
                :show-search-btn="show.searchBtn"
                :show-introduction-btn="introductionData &&
                    Object.keys(this.introductionData).length > 0
                    "
                :indicator-state="sideBarNaviIndicatorState"
                :show-gallery-btn="mapData.show_gallery"
                :go-to-main-map="goToMainMap"
                :banner-url="mapData.banner_url"
                :banner-link="mapData.banner_link"
                :current-locale="mapData.current_locale"
                :show-switch-language="mapData.is_multi_localization"
                :show-route-btn="showRouteBtn"
                :show-floor-direction-btn="useShortestPath"
                :currentCategoryTitle="currentCategoryTitle"
            ></side-bar-navi>
            <sub-side-bar
                :default-state="isShowLocationDetail ? 'open' : subSideBarState"
                :is-show-location-detail="isShowLocationDetail ? true : false"
                @closedLocationDetail="closeLocationDetail"
                @changedState="changeState"
            >
                <location-list-box
                    v-show="open.locationListBox"
                    :location-list="locationsInListBox"
                    :my-smart-map="mySmartMap"
                    :parcel-resource="[parcelResource]"
                    :is-on-parcel-map="true"
                    :current-layer="currentLayer"
                    @closed="closeSubSidebar('locationListBox')"
                    @changedLocationDetail="changeLocationDetail"
                    @changedLayerId="changeLayerId"
                ></location-list-box>
                <locale-box
                    v-show="open.localeBox"
                    :locales="mapData.locales"
                    @closed="open.localeBox = false"
                ></locale-box>
                <introduction
                    v-show="open.introductionBox"
                    @closed="open.introductionBox = false"
                    :introduction-data="introductionData"
                    :trans="trans"
                ></introduction>
                <gallery
                    v-show="open.galleryBox"
                    @closed="open.galleryBox = false"
                    :trans="trans"
                    :location-resource="floorGuideLocations"
                    @changedLocationDetail="changeLocationDetail"
                    :is-on-parcel-map="true"
                    :current-layer="currentLayer"
                    @changedLayerId="changeLayerId"
                ></gallery>
                <location-detail
                    :location="currentLocation.properties"
                    :location-list="currentLocations"
                    :coordinates="currentLocation.geometry.coordinates"
                    :defaultLocale="mapData.current_locale"
                    :useAudioGuide="mapData.client.use_audio_guide"
                    :useCongestion="mapData.client.use_congestion"
                    :use-google-map-directions="useGoogleMapDirections"
                    :use-shortest-path="useShortestPath"
                    :useQRCode="mapData.client.use_qr_code"
                    :hashId="mapData.hash_id"
                    :hideNearSpots="mapData.hide_near_spots"
                    @closed="closeLocationDetail"
                    @changedLocationDetail="changeLocationDetail"
                    v-if="currentLocation"
                    @clickTag="updateCurrentTag"
                    :parcelAndLayerTitle="parcelResource.title"
                    :current-layer-title="currentLayer.title"
                    :current-state="currentState"
                    :is-opened-floor-guide-box="isOpenedFloorGuideBox"
                    :has-floor-guide-box="true"
                    @toggledSubSidebar="openSubSidebar"
                    @selectedCategory="selectCategory"
                    @openedShortestPath="openShortestPath"
                    :category-id="categoryId"
                ></location-detail>
                <map-setting
                    v-if="mySmartMap && open.mapSetting"
                    :map-data="mapData"
                    :my-smart-map="mySmartMap"
                    :show="show"
                    :lang="currentLang"
                    :switch-language="switchLanguage"
                    @closed="open.mapSetting = false"
                    @changedTheme="(payload) => (currentTheme = payload)"
                ></map-setting>
                <user-guide
                    v-if="open.userGuide"
                    :show="show"
                    @closed="open.userGuide = false"
                ></user-guide>
                <attribution-license
                    v-if="open.attributionLicense"
                    @closed="open.attributionLicense = false"
                    :map-data="mapData"
                ></attribution-license>
                <routes
                    v-show="mySmartMap && open.routeBox"
                    @closed="open.routeBox = false"
                    :show-list-route="open.routeBox"
                    :my-smart-map="mySmartMap"
                    :filterLocationsByRoute="filterLocationsByRoute"
                    @showRouteBtn="(payload) => (showRouteBtn = payload)"
                    :trans="trans"
                    @toggledSubSidebar="openSubSidebar"
                    :route-locations="routeLocations"
                    @chooseLocation="(payload) => onClickMarker(payload)"
                    :currentLayer="currentLayer"
                ></routes>
                <floor-guide-box
                    ref="floorGuideBoxComponent"
                    v-show="open.floorGuideBox"
                    @closed="open.floorGuideBox = false"
                    :location-resource="floorGuideLocations"
                    :item-resource="itemResource"
                    :facility-resource="facilityResource"
                    :category-data="categoryData"
                    :number-of-result="locationsInFloorGuide.length"
                    @filtered="filterLocations"
                    @showedLocationsList="open.locationListBox = true"
                    @toggledSubSidebar="openSubSidebar"
                    @isClickedLocationsFound="(payload) => (isClickedLocationsFound = payload)
                        "
                    :current-tag="currentTag"
                    :location-list="locationsInFloorGuide"
                    @changedLocationDetail="changeLocationDetail"
                    :current-state="currentState"
                    :layers="parcelResource.layers"
                    :parcel-title="parcelResource.title"
                    @changedLayer="changeLayer"
                    :current-layer="currentLayer"
                    @isOpenedFloorGuideBox="(payload) => (isOpenedFloorGuideBox = payload)
                        "
                    @changedCategory="changeCategory"
                    :show-image-category="mapData.show_image_category"
                ></floor-guide-box>
                <template v-if="useShortestPath && mySmartMap">
                    <floor-direction
                        v-show="open.floorDirection"
                        :location-resource="locationResource"
                        :status="open.floorDirection"
                        :parcel-resource="parcelResource"
                        :my-smart-map="mySmartMap"
                        :use-maplibre="useMaplibre"
                        :current-layer="currentLayer"
                        :facility-resource="facilityResource"
                        :geolocate-control="geolocateControl"
                        @changedLayerId="changeLayerId"
                        @closed="open.floorDirection = false"
                        @hideFloorDirection="(payload) => (open.floorDirection = false)"
                        @outofmaxbounds="displayOutOfMap = true"
                        :onMoveend="onMoveend"
                        ref="floorDirection"
                    ></floor-direction>
                </template>
            </sub-side-bar>
            <bottom-sheet-category
                :current-sheet="open.categoryBox ? 'categorysheet' : ''"
                :map-id="mapData.id"
                :locale="mapData.current_locale != mapData.default_locale
                    ? mapData.current_locale
                    : ''
                    "
                :categories="categoryData"
                @changedCategoryId="updateCurrentCategoryId"
                @closed="closeSubSidebar('categoryBox')"
                v-if="mySmartMap && open.categoryBox"
                :trans="trans"
                :currentCategoryId="currentCategoryId"
            ></bottom-sheet-category>
            <div class="absolute top-20 left-1 sm:left-24 m-1 z-20 mb-2">
                <layer-list
                    :layers="parcelResource.layers"
                    :current-layer="currentLayer"
                    @changedLayer="changeLayer"
                    :layer-title="currentLayer.title"
                    :parcel-title="parcelResource.title"
                    :go-to-main-map="goToMainMap"
                    :trans="trans"
                ></layer-list>
            </div>
            <div class="absolute bottom-16 right-0 flex grid grid-cols-1 gap-2 m-2">
                <button
                    class="btn btn-sm shadow-sm border border-base-content/5 w-10 h-10 btn-compass"
                    @click="toggleFloorLayers()"
                    v-if="mapData.show_floor_map"
                >
                    <span class="material-symbols-outlined">map</span>
                </button>
                <div class="join join-vertical shadow-sm w-10 z-10">
                    <button
                        class="btn join-item border border-base-content/5"
                        @click="onClickZoom(1)"
                    >
                        <a>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                />
                            </svg>
                        </a>
                    </button>
                    <button
                        class="btn join-item shadow-sm border border-base-content/5"
                        @click="onClickZoom(-1)"
                    >
                        <a>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M18 12H6"
                                />
                            </svg>
                        </a>
                    </button>
                </div>
                <button
                    class="btn btn-sm shadow-sm border border-base-content/5 w-10 h-10 btn-compass"
                    v-show="show.geolocateBtn"
                ></button>
                <button
                    class="btn btn-sm shadow-sm border border-base-content/5 w-10 h-10"
                    v-show="show.geolocateBtn"
                    @click="triggerGeolocate"
                >
                    <span
                        class="material-symbols-outlined notranslate fill-current"
                        v-if="!isGeolocateLoading"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="24"
                            viewBox="0 -960 960 960"
                            width="24"
                        >
                            <path
                                d="M516-120 402-402 120-516v-56l720-268-268 720h-56Zm26-148 162-436-436 162 196 78 78 196Zm-78-196Z"
                            />
                        </svg>
                    </span>
                    <span
                        class="loading loading-spinner"
                        v-else
                    ></span>
                </button>
            </div>
            <div
                class="absolute bottom-12 right-0 flex grid grid-cols-1 gap-2 m-2 card w-96 bg-base-100 shadow-xl"
                v-if="displayOutOfMap"
            >
                <div class="card-body py-5">
                    <h2 class="card-title text-base justify-center">{{ trans.alert.use_within_map_area }}</h2>
                    <div class="card-actions justify-center mt-2">
                        <button
                            class="btn btn-secondary btn-sm px-5"
                            @click="displayOutOfMap = false"
                        >OK</button>
                    </div>
                </div>
            </div>
            <div
                class="tootip bg-base-100 white_box"
                v-show="tooltip.title"
                :style="{ top: tooltip.top, left: tooltip.left }"
            >
                <p class="ttl">{{ tooltip.title }}</p>
            </div>
            <div style="display: none">
                <div
                    class="parcel-marker"
                    ref="parcelMarkerTemplate"
                >
                    <div class="mask mask-squircle bg-base-100 h-14 w-14 marker-p">
                        <img
                            src
                            alt
                            class="mask mask-squircle w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <div class="text-xs font-bold badge absolute text-primary w-max mt-1 max-content parcel-title"></div>
                </div>
            </div>
            <div style="display: none">
                <div
                    class="bubble pin bg-primary h-12 w-12 rounded-full p-1"
                    ref="locationMarkerTemplate"
                >
                    <a
                        href="javascript:void(0)"
                        class="bubble_link"
                    >
                        <img
                            loading="lazy"
                            class="inline rounded-full"
                        />
                    </a>
                    <div class="stamp_group"></div>
                    <div id="currentCongestionTooltip"></div>
                </div>
                <div
                    class="bubble bg-secondary h-12 w-12 rounded-full p-1"
                    ref="clusterMarkerTemplate"
                >
                    <a
                        href="javascript:void(0)"
                        class="bubble_link"
                    >
                        <img
                            loading="lazy"
                            class="inline rounded-full"
                        />
                    </a>
                    <div class="count_box"></div>
                </div>
            </div>
            <div
                id="currentCongestion"
                style="display: none"
            >
                <div
                    v-for="(n, i) in 6"
                    :key="i"
                    :id="'congestion-level-' + i"
                >
                    <congestion-icon-level
                        :level="i"
                        :type="1"
                        :key="i"
                    ></congestion-icon-level>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import MapBox from "../mixins/MapBox/Map";
import LocationDetail from "./LocationDetail";
import LocationMarker from "./LocationMarker";
import * as turf from "@turf/turf";
import Init from "../mixins/Init";
import SearchBox from "./SearchBox";
import LocationListBox from "./LocationListBox";
import MapSetting from "./MapSetting";
import UserGuide from './UserGuide';
import AttributionLicense from './AttributionLicense';
import ParcelNav from "./ParcelNav";
import Category from "./Category";
import SideMenu from "./SideMenu";
import LayerList from "./LayerList";
import { default as MarkerHelper } from "../mixins/Markers";
import { default as URLSearchParamsHelper } from "../mixins/URLSearchParams";
import Maplibre from "../mixins/MapBox/Maplibre";
import CongestionIconLevel from "./CongestionIconLevel";
import SideBarNavi from "./SideBarNavi";
import SubSideBar from "./SubSideBar";
import LocaleBox from "./LocaleBox";
import BottomSheet from "./BottomSheet";
import BottomSheetCategory from "./BottomSheetCategory";
import Introduction from "./Introduction";
import Routes from "./Routes";
import Gallery from "./Gallery";
import { default as SubSideBarHelper } from "../mixins/SubSideBar";
import { default as IntroductionHelper } from "../mixins/Introduction";
import { default as CurrentTagHelper } from "../mixins/CurrentTag";
import { default as FilterHelper } from "../mixins/Filter";
import FloorGuideBox from "./FloorGuideBox";
import { default as ParcelHelper } from "../mixins/ParcelMarker";
import FloorDirection from "./FloorDirection";
import { default as HeatMapHelper } from '../mixins/HeatMap';

export default {
    provide() {
        return {
            displayLink: this.displayLink,
            parcelMapLink: this.parcelMapLink,
            trans: this.trans,
            locale: this.mapData.current_locale,
            mapId: this.mapData.id,
            currentMapLink: this.currentMapLink,
            isHideCategoryFilter: this.isHideCategoryFilter
        };
    },
    components: {
        LocationMarker,
        LocationDetail,
        SearchBox,
        LocationListBox,
        MapSetting,
        UserGuide,
        ParcelNav,
        Category,
        SideMenu,
        CongestionIconLevel,
        LayerList,
        SideBarNavi,
        SubSideBar,
        LocaleBox,
        BottomSheetCategory,
        BottomSheet,
        Introduction,
        Routes,
        Gallery,
        FloorGuideBox,
        FloorDirection,
        AttributionLicense,
    },
    mixins: [
        MapBox,
        Init,
        MarkerHelper,
        Maplibre,
        URLSearchParamsHelper,
        SubSideBarHelper,
        IntroductionHelper,
        CurrentTagHelper,
        FilterHelper,
        ParcelHelper,
        HeatMapHelper,
    ],
    props: {
        parcelResource: {
            type: Object,
            required: true,
        },
        locationResource: {
            type: Array,
            required: true,
        },
        itemResource: {
            type: Array,
            default: function () {
                return [];
            },
        },
        facilityResource: {
            type: Array,
            default: function () {
                return [];
            },
        },
        mapData: {
            type: Object,
            required: true,
        },
        categoryData: {
            type: Array,
            default: function () {
                return [];
            },
        },
        displayLink: {
            type: String,
            required: true,
        },
        parcelMapLink: {
            type: String,
            required: true,
        },
        trans: {
            type: Object,
            required: true,
        },
        otherParcels: {
            type: Array,
            default: function () {
                return [];
            },
        },
        categoryId: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            currentLayer: null,
            currentLocation: null,
            currentCategoryId: this.categoryId,
            currentLang: this.mapData.default_language,
            currentTheme: this.mapData.theme_color,
            tooltip: {
                title: "",
                top: "",
                left: "",
            },
            show: {
                searchBtn: this.mapData.apply_geocoder,
                zoomBtn: true,
                geolocateBtn: this.mapData.apply_locate_user,
                refreshBtn: true,
                categoryBox: true,
            },
            isOutOfMaxbounds: false,
            displayOutOfMap: false,
            filter: null,
            isHoverLocationMarker: false,
            isGeolocateLoading: false,
            isShowLocationDetail: false,
            currentLocations: [],
            currentState: "",
            isOpenedFloorGuideBox: false,
            floorGuideLocations: [],
            showRouteBtn: false,
            showFloorMapBtn: this.mapData.show_floor_map,
        };
    },
    methods: {
        closeLocationDetail() {
            this.isShowLocationDetail = false;
        },
        toggleFloorLayers() {
            this.showFloorMapBtn = !this.showFloorMapBtn;
            const visibility = this.showFloorMapBtn ? 'visible' : 'none';
            const tileLayers = ['tile-layer-source', 'tile-layer-source-parcel'];

            const setLayerVisibility = (layerId) => {
                if (this.mySmartMap.getLayer(layerId)) {
                    this.mySmartMap.setLayoutProperty(layerId, 'visibility', visibility);
                }
            };

            tileLayers.forEach(setLayerVisibility);

            this.currentLayer.imageries.forEach((imagery) => {
                const layerId = this.imageryLayerIdPrefix + imagery.id;
                setLayerVisibility(layerId);
            });
            setLayerVisibility("bg-layer");
        },
        loadImageries() {
            if (this.currentLayer.imageries.length == 0) return;
            var self = this;
            this.currentLayer.imageries.forEach((imagery) => {
                const sourceId = "imagery-source-" + imagery.id;
                const layerId = this.imageryLayerIdPrefix + imagery.id;

                if (!self.mySmartMap.getSource(sourceId)) {
                    self.mySmartMap.addSource(sourceId, {
                        type: "image",
                        url: imagery.image_url,
                        coordinates: imagery.coordinates,
                    });
                }
                if (!self.mySmartMap.getLayer(layerId)) {
                    self.mySmartMap.addLayer({
                        id: layerId,
                        type: "raster",
                        source: sourceId,
                        paint: {
                            "raster-fade-duration": 0,
                        },
                    });
                } else {
                    self.mySmartMap.setLayoutProperty(
                        layerId,
                        "visibility",
                        "visible"
                    );
                }
            });
        },
        moveImageries() {
            if (
                !this.mySmartMap.getLayer("bg-layer") ||
                this.currentLayer.imageries.length == 0
            )
                return;

            var self = this;
            this.currentLayer.imageries.forEach((imagery) => {
                const layerId = this.imageryLayerIdPrefix + imagery.id;
                self.mySmartMap.moveLayer("bg-layer", layerId);
            });
        },
        onClickMarker(payload) {
            const found = this.locationResource.find(
                (element) => element.properties.id == payload
            );
            if (!found) return;

            this.hideLeftMapSections("");
            if (this.useShortestPath) {
                this.$refs.floorDirection.close();
            }
            this.currentLocation = found;
            this.mySmartMap.flyTo({
                center: this.currentLocation.geometry.coordinates,
                speed: 2.4,
                curve: 1.2,
            });
            var self = this;
            this.mySmartMap.once("idle", () => {
                self.isShowLocationDetail = true;
                self.updateCurrentLocations();
            });
        },
        loadLayerFacilities() {
            var self = this;
            var facilityLength = this.currentLayer.layer_facilities.length;
            for (let i = 0; i < facilityLength; i++) {
                let item = this.currentLayer.layer_facilities[i];
                if (item.hide_map) continue;

                const sourceId = "facility-source-" + item.id;
                const layerId = "facility-layer-" + item.id;

                if (!self.mySmartMap.getSource(sourceId)) {
                    self.mySmartMap.addSource(sourceId, {
                        type: "image",
                        url: item.image_url,
                        coordinates: item.coordinates,
                    });
                }
                if (!self.mySmartMap.getLayer(layerId)) {
                    self.mySmartMap.addLayer({
                        id: layerId,
                        type: "raster",
                        source: sourceId,
                        paint: {
                            "raster-fade-duration": 0,
                        },
                    });
                } else {
                    self.mySmartMap.setLayoutProperty(
                        layerId,
                        "visibility",
                        "visible"
                    );
                }
            }
        },
        onFacilityPoints() {
            var self = this;
            this.mySmartMap.on("mousemove", (e) => {
                if (self.isHoverLocationMarker) return;
                var isOnFacility = false;
                var found;
                self.currentLayer.layer_facilities.forEach((item) => {
                    var pt = turf.point([e.lngLat.lng, e.lngLat.lat]);
                    var poly = turf.polygon([
                        item.coordinates.concat([item.coordinates[0]]),
                    ]);
                    if (
                        item.show_tooltip &&
                        turf.booleanPointInPolygon(pt, poly)
                    ) {
                        isOnFacility = true;
                        found = item;
                    }
                });
                if (isOnFacility) {
                    let facility = self.facilityResource.find(el => el.id == found.facility_id);
                    self.tooltip = {
                        title: !facility ? found.facility_title : facility.title,
                        top: e.point.y + 20 + "px",
                        left: e.point.x + 20 + "px",
                    };
                } else {
                    self.tooltip = {
                        title: "",
                        top: "",
                        left: "",
                    };
                }
            });
        },
        filterLocations(payload) {
            this.filter = payload;
            if (this.isFiltered) {
                this.currentCategoryId = 0;
            }
        },
        changeLocationDetail(payload) {
            var self = this;
            var zoomLevel =
                this.mySmartMap.getZoom() >= 17
                    ? this.mySmartMap.getZoom()
                    : 17;
            this.isShowLocationDetail = false;
            this.currentLocation = null;
            this.$nextTick(() => {
                this.mySmartMap.flyTo({
                    center: payload.geometry.coordinates,
                    zoom: zoomLevel,
                    speed: 1.8,
                });
                this.mySmartMap.once("idle", () => {
                    self.isShowLocationDetail = true;
                    self.currentLocation = payload;
                    self.updateCurrentLocations();
                });
            });
        },
        onClickZoom(value) {
            var newZoom = this.currentZoomLevel + value;
            if (newZoom < this.mySmartMap.getMinZoom()) return;

            this.currentZoomLevel = newZoom;
        },
        goToMainMap() {
            let parts = window.location.pathname.split("/");
            let locale = parts[parts.length - 1];
            window.location.href =
                this.displayLink +
                (this.mapData.locales.includes(locale) ? `/${locale}` : "") +
                (!this.categoryId ? '' : '?c=' + this.categoryId);
        },
        triggerGeolocate() {
            if (this.isOutOfMaxbounds) {
                this.displayOutOfMap = true;
                return;
            }
            if (this.mapData.client.use_heat_map) {
                this.handlePositionHeatMap('parcel_' + this.parcelResource.id);
            }
            const geolocateBtn = document.querySelector(
                "button.mapboxgl-ctrl-geolocate"
            );
            var self = this;

            navigator.geolocation.getCurrentPosition(
                (position) => { },
                (error) => {
                    alert(self.trans.alert.allow_to_access_your_location);
                    setTimeout(() => {
                        self.isGeolocateLoading = false;
                    }, 10000);
                    return;
                }
            );

            geolocateBtn.click();
            this.isGeolocateLoading = geolocateBtn.classList.contains(
                "mapboxgl-ctrl-geolocate-waiting"
            );
            setTimeout(() => {
                this.isGeolocateLoading = false;
            }, 3500);
            this.geolocateControl.once("outofmaxbounds", () => {
                self.isOutOfMaxbounds = true;
                self.displayOutOfMap = true;
            });
        },
        toggleMenu(type) {
            this.open[type] = !this.open[type];
        },
        updateTooltip(payload) {
            this.tooltip = payload;
            this.isHoverLocationMarker = this.tooltip.title != "";
        },
        changeLayer(payload) {
            this.currentLayer = payload;
            this.addParcelMarkers(this.subParcels);
        },
        changeLayerId(layerId) {
            this.currentLayer = this.parcelResource.layers.find(
                (el) => el.id == layerId
            );
        },
        handleWhenHasSpotID() {
            const spotID = this.getSpotId();
            if (!spotID) {
                return;
            }

            this.onClickMarker(spotID);
            this.currentLayer = this.parcelResource.layers.find(
                (layer) => layer.id == this.currentLocation.properties.layer_id
            );
        },
        onMoveend() {
            if (this.currentLocation) {
                return;
            }

            const zoom = this.mySmartMap.getZoom();
            this.currentZoomLevel = zoom;
            this.updateCurrentLocations();
            this.addParcelMarkers(this.subParcels);
        },
        updateCurrentCategoryId(val) {
            this.currentCategoryId = val;
            this.$refs.floorGuideBoxComponent.clearAllFilter();
        },
        updateCurrentLocations() {
            if (!this.currentLayer) return [];
            var result = this.locationResource.filter(
                (el) =>
                    el.properties.layer_id == this.currentLayer.id &&
                    (!this.currentCategoryId ||
                        el.properties.category.id == this.currentCategoryId)
            );

            if (this.isFiltered) {
                result = result.filter((el1) => {
                    return this.filteredLocations.some((el2) => {
                        return el1.properties.id == el2.properties.id;
                    });
                });
            }

            this.currentLocations = result;
        },
        changeState(payload) {
            this.currentState = payload;
        },
        updateFloorGuideLocations() {
            var result = this.locationResource;

            if (this.isFiltered) {
                result = result.filter((el1) => {
                    return this.filteredLocations.some((el2) => {
                        return el1.properties.id == el2.properties.id;
                    });
                });
            }

            this.floorGuideLocations = result;
        },
        selectCategory(payload) {
            if (payload) {
                this.$refs.floorGuideBoxComponent.handleClickedMainCategory(
                    payload
                );
            }
        },
        changeCategory(val) {
            this.currentCategoryId = val;
        },
        closeSubSidebar(payload) {
            this.open[payload] = false;
            this.currentOpenSubSidebar = "";
        },
        filterLocationsByRoute(route) {
            if (!route) {
                this.resetFilter();
                return;
            }
            this.currentCategoryId = 0;
            this.filterLocations({
                keyword: "",
                categories: !this.categoryId ? [] : [this.categoryId],
                item: "",
                facilities: [],
                areas: [],
                route_location_ids: route.location_ids,
            });
        },
        resetFilter() {
            this.filter = {
                keyword: "",
                categories: !this.categoryId ? [] : [this.categoryId],
                item: "",
                facilities: [],
                areas: [],
            };
        },
        isShowParcelMaker(parcel) {
            return (
                this.currentLayer.id == parcel.floor_id &&
                this.currentZoomLevel >= parcel.zoom_range[0] &&
                this.currentZoomLevel < parcel.zoom_range[1]
            );
        },
        goToParentParcelMap() {
            if (this.parentId) {
                const mapHashID = window.location.pathname.split("/")[2];
                var locale = window.location.pathname.split("/")[5];
                var href = locale
                    ? `/maps/${mapHashID}/parcels/${this.parentId}/${locale}`
                    : `/maps/${mapHashID}/parcels/${this.parentId}`;
                window.open(!this.categoryId ? href : href + '?c=' + this.categoryId, "_self");
            }
        },
        openShortestPath(locationId) {
            this.$refs.floorDirection.chooseDestination(locationId);
        },
        initFloorDirection() {
            const params = new URLSearchParams(window.location.search)
            const open = params.get('openFloorDirection');
            if (!open) return;

            this.open.floorDirection = open == 'true' ? true : false;
        }
    },
    created() {
        this.currentLayer =
            this.parcelResource.layers.find(
                (layer) => layer.id === this.parcelResource.default_layer_id
            ) ?? this.parcelResource.layers[0];
    },
    mounted() {
        this.updateCurrentLocations();
        this.updateFloorGuideLocations();
        this.mySmartMap.on('moveend', this.onMoveend);
        this.initBackgroundPattern();
        this.initCss();
        this.mySmartMap.setZoom(this.parcelResource.min_zoom);
        const bounds = this.mySmartMap.getBounds();
        this.mySmartMap.setMaxBounds(bounds);
        var self = this;
        this.mySmartMap.on("load", function () {
            self.mySmartMap.setZoom(self.zoomLevel);
            self.loadImageries();
            self.loadLayerFacilities();
            self.onFacilityPoints();
            self.switchLanguage(self.mapData.current_locale);
            self.initFloorDirection();
        });
        // this.mySmartMap.on("styledata", function (e) {
        //     console.log("styledata ne", e);
        //     self.loadImageries();
        //     self.loadLayerFacilities();
        //     self.onFacilityPoints();
        // });
        this.mySmartMap.once("idle", function () {
            self.moveImageries();
            self.handleWhenHasSpotID();
        });

        var self = this;
        document
            .getElementsByClassName("mapboxgl-canvas")[0]
            .addEventListener("click", function () {
                self.closeLocationDetail();
            });
    },
    watch: {
        currentLayer(newVal, oldVal) {
            if (!oldVal) {
                return;
            }
            var self = this;
            if (oldVal.imageries.length > 0) {
                const oldImageryLayerIdPrefix = `imagery-layer-${oldVal.id}-`;
                oldVal.imageries.forEach((oldImagery) => {
                    if (
                        self.mySmartMap.getLayer(
                            oldImageryLayerIdPrefix + oldImagery.id
                        )
                    ) {
                        self.mySmartMap.setLayoutProperty(
                            oldImageryLayerIdPrefix + oldImagery.id,
                            "visibility",
                            "none"
                        );
                    }
                });
            }
            oldVal.layer_facilities.forEach((item) => {
                if (!item.hide_map) {
                    self.mySmartMap.setLayoutProperty(
                        "facility-layer-" + item.id,
                        "visibility",
                        "none"
                    );
                }
            });
            this.loadImageries();
            this.loadLayerFacilities();
            this.onFacilityPoints();
            this.updateCurrentLocations();
            this.updateFloorGuideLocations();
        },
        isShowLocationDetail(newVal) {
            this.$nextTick(() => {
                if (newVal) {
                    this.hideAllMarkersExcept(
                        this.currentLocation.properties.id
                    );
                    this.hideLeftMapSections("locationDetail");
                    this.setSpotId(this.currentLocation.properties.id);
                } else {
                    this.currentLocation = null;
                    this.showAllMarkers();
                    this.deleteSpotId();
                    this.openSubSidebar(this.currentOpenSubSidebar);
                }
            });
        },
        currentLocations(newVal, oldVal) {
            oldVal.forEach((el) => {
                if (!newVal.some(n => n.properties.id == el.properties.id)) {
                    this.removeLocationMarker(el.properties.id);
                } else {
                    this.handleRotateMarker(el);
                }
            });

            newVal.forEach((el) => {
                if (!oldVal.some(n => n.properties.id == el.properties.id)) {
                    this.addLocationMarker(el);
                } else if (this.mapData.show_tooltip && el.properties.marker_style != 'illustration') {
                    this.addLocationMarkerTooltip(el);
                }
            });
        },
        currentCategoryId() {
            this.updateCurrentLocations();
        },
        filter: {
            deep: true,
            handler: function () {
                this.updateCurrentLocations();
                this.updateFloorGuideLocations();
            },
        },
    },
    computed: {
        sideBarNaviIndicatorState() {
            if (this.filter) {
                for (const key in this.filter) {
                    if (key === "route_location_ids") {
                        return "route";
                    }
                    if (this.filter[key].length != 0) {
                        return "search";
                    }
                }
            }
            if (this.currentCategoryId) {
                return "category";
            }
            return "";
        },
        filteredLocations() {
            if (!this.isFiltered) {
                return [];
            }
            var result = [];
            const locationLength = this.locationResource.length;
            for (let i = 0; i < locationLength; i++) {
                const currentLocation = this.locationResource[i];
                const properties = currentLocation.properties;

                if (this.filter.route_location_ids) {
                    if (this.filter.route_location_ids.includes(properties.id)) {
                        result.push(currentLocation);
                    }
                    continue;
                }

                if (
                    !this.filter.keyword &&
                    this.filter.categories.length == 0 &&
                    !this.filter.item &&
                    this.filter.facilities.length == 0
                ) {
                    result.push(currentLocation);
                    continue;
                }

                if (this.filter.keyword) {
                    var isMatchedKeyword = this.searchByKeyword(
                        properties,
                        this.filter.keyword
                    );
                    if (!isMatchedKeyword) continue;
                }
                if (
                    (this.filter.categories.length > 0 &&
                        this.filter.categories.indexOf(properties.category.id) == -1)
                    || (this.categoryId &&
                        ((!properties.category.upper_category_id && this.categoryId != properties.category.id)
                            || (properties.category.upper_category_id && this.categoryId != properties.category.upper_category_id)))
                ) {
                    continue;
                }
                if (this.filter.item) {
                    const itemFound = properties.items.find(
                        (item) => item.id == this.filter.item
                    );
                    if (!itemFound) continue;
                }
                if (this.filter.facilities.length > 0) {
                    var isMatchedFacility = false;
                    const facilityLength = properties.facilities.length;
                    for (let i = 0; i < facilityLength; i++) {
                        const facility = properties.facilities[i];
                        if (this.filter.facilities.indexOf(facility.id) != -1) {
                            isMatchedFacility = true;
                            break;
                        }
                    }
                    if (!isMatchedFacility) continue;
                }
                result.push(currentLocation);
            }

            return result;
        },
        isFiltered() {
            return this.filter &&
                (this.filter.keyword ||
                    this.filter.item ||
                    this.filter.categories.length > 0 ||
                    this.filter.facilities.length > 0 ||
                    (this.filter.route_location_ids &&
                        this.filter.route_location_ids.length > 0))
                ? true
                : false;
        },
        locationsInListBox() {
            return this.isFiltered
                ? this.filteredLocations
                : this.currentLocations;
        },
        isShowMarkersWhenFiltered() {
            return (
                this.isFiltered ||
                this.currentCategoryId ||
                this.isClickedLocationsFound
            );
        },
        locationsInFloorGuide() {
            return this.isFiltered
                ? this.filteredLocations
                : this.floorGuideLocations;
        },
        routeLocations() {
            const { filter, locationResource } = this;
            return filter?.route_location_ids?.length > 0
                ? locationResource.filter((location) =>
                    filter.route_location_ids.includes(location.properties.id)
                )
                : [];
        },
        subParcels() {
            let result = [];
            this.otherParcels.forEach((item) => {
                this.parcelResource.layers.forEach((element) => {
                    if (element.id == item.floor_id) {
                        result.push(item);
                    }
                });
            });
            return result;
        },
        parentId() {
            if (this.otherParcels.length == 0 || !this.parcelResource.floor_id)
                return;

            let parentId = "";
            let parcelLength = this.otherParcels.length;
            for (let i = 0; i < parcelLength; i++) {
                let layers = this.otherParcels[i].layers;
                for (let j = 0; j < layers.length; j++) {
                    if (layers[j].id === this.parcelResource.floor_id) {
                        parentId = this.otherParcels[i].id;
                        break;
                    }
                }
            }
            return parentId;
        },
        imageryLayerIdPrefix() {
            return `imagery-layer-${this.currentLayer.id}-`
        },
        isHideCategoryFilter() {
            return this.categoryId != 0;
        },
        currentCategoryTitle() {
            let currentCategory = this.categoryData.find((cat) => cat.id == this.categoryId);
            return currentCategory ? currentCategory.title : '';
        }
    },
};
</script>
